import React, { useState, useEffect, useMemo, useCallback } from 'react';

export default function MedidasDisciplinarias({ usuario, onBackToMenu }) {
  const API_URL = process.env.REACT_APP_API_URL;

  const [medidas, setMedidas] = useState([]);
  const [sedesCatalogo, setSedesCatalogo] = useState([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [buscandoEmp, setBuscandoEmp] = useState(false);
  const [evidenciaModal, setEvidenciaModal] = useState(null);
  const [filtroSedeUI, setFiltroSedeUI] = useState('');
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [notificacion, setNotificacion] = useState({ texto: '', tipo: '' });

  // Estado para controlar qué filas tienen las observaciones expandidas
  const [observacionesExpandidas, setObservacionesExpandidas] = useState({});

  const [formData, setFormData] = useState({
    documento_empleado: '',
    nombres_empleado: '',
    cargo: '',
    sede: '',
    tipo_medida: 'Llamado de Atención',
    observaciones: '',
    fecha_incidencia: new Date().toISOString().split('T')[0],
  });
  const [archivoBase64, setArchivoBase64] = useState('');
  const [nombreArchivo, setNombreArchivo] = useState('');

  const mostrarAviso = (texto, tipo = 'success') => {
    setNotificacion({ texto, tipo });
    setTimeout(() => setNotificacion({ texto: '', tipo: '' }), 4500);
  };

  const toggleExpandirObs = (id) => {
    setObservacionesExpandidas(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // =========================================================
  // 1. EVALUACIÓN DE PERMISOS Y ALCANCE DE SEDE (ABAC)
  // =========================================================
  const configABAC = useMemo(() => {
    const cargoUpper = String(usuario?.cargo || usuario?.rol || '').toUpperCase().trim();
    const esSuperAdmin = cargoUpper.includes('SUPER_ADMIN') || cargoUpper === 'SUPERADMIN';

    let parsed = {};
    try {
      parsed = typeof usuario?.permisos_avanzados === 'string'
        ? JSON.parse(usuario.permisos_avanzados)
        : (usuario?.permisos_avanzados || {});
    } catch (e) {
      parsed = {};
    }

    const micros = parsed.micros || parsed || {};
    const reglaModulo =
      micros['Medidas Disciplinarias'] ||
      micros['medidas_disciplinarias'] ||
      micros['Gestión Humana'] ||
      {};

    const rawAlcance = String(
      reglaModulo.alcance || reglaModulo.scope || (esSuperAdmin ? 'GLOBAL' : 'PROPIA')
    ).toUpperCase().trim();

    const esGlobal =
      esSuperAdmin ||
      rawAlcance === 'GLOBAL' ||
      rawAlcance.includes('TODAS') ||
      rawAlcance.includes('CONTROL TOTAL') ||
      Boolean(micros['Mostrar Todas las Sedes']);

    const esEspecifica =
      !esGlobal && (rawAlcance === 'ESPECIFICA' || rawAlcance.includes('ESPECIFIC'));

    const sedesIdsPermitidas = Array.isArray(reglaModulo.sedes_ids)
      ? reglaModulo.sedes_ids.map((s) => String(s).toUpperCase().trim())
      : Array.isArray(reglaModulo.sedes)
      ? reglaModulo.sedes.map((s) => String(s).toUpperCase().trim())
      : [];

    const sedePropiaUsuario = String(
      usuario?.sede_nombre ||
      usuario?.sede ||
      usuario?.centroTrabajo ||
      usuario?.centro_trabajo ||
      usuario?.area_nombre ||
      ''
    ).toUpperCase().trim();

    const idSedePropia = usuario?.sede_id ? String(usuario.sede_id).trim() : '';

    const puedeCrear =
      esSuperAdmin ||
      reglaModulo === true ||
      reglaModulo?.crear !== false;

    return {
      esGlobal,
      esEspecifica,
      sedesIdsPermitidas,
      sedePropiaUsuario,
      idSedePropia,
      puedeCrear,
      micros
    };
  }, [usuario]);

  const sedeEstaAutorizada = useCallback((nombreSedeRegistro, idSedeRegistro = '') => {
    if (configABAC.esGlobal) return true;

    const sedReg = String(nombreSedeRegistro || '').toUpperCase().trim();
    const idReg = String(idSedeRegistro || '').toUpperCase().trim();

    if (configABAC.esEspecifica && configABAC.sedesIdsPermitidas.length > 0) {
      return configABAC.sedesIdsPermitidas.some((perm) => {
        const sedeEnCatalogo = sedesCatalogo.find(
          (s) => String(s.id) === perm || String(s.prefijo || '').toUpperCase() === perm || String(s.nombre || '').toUpperCase() === perm
        );
        const nombreCatalogo = sedeEnCatalogo ? String(sedeEnCatalogo.nombre).toUpperCase().trim() : perm;
        return (
          (idReg && idReg === perm) ||
          (sedReg && (sedReg.includes(nombreCatalogo) || nombreCatalogo.includes(sedReg)))
        );
      });
    }

    if (configABAC.sedePropiaUsuario) {
      return (
        sedReg.includes(configABAC.sedePropiaUsuario) ||
        configABAC.sedePropiaUsuario.includes(sedReg)
      );
    }
    if (configABAC.idSedePropia && idReg) {
      return idReg === configABAC.idSedePropia;
    }

    return true;
  }, [configABAC, sedesCatalogo]);

  // =========================================================
  // 2. CARGA DE DATOS CON FALLBACK MULTIRUTA V2
  // =========================================================
  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'x-user-data': JSON.stringify(usuario || {}),
    'x-abac-permissions': JSON.stringify(configABAC.micros || {})
  }), [usuario, configABAC.micros]);

  const cargarMedidas = useCallback(async () => {
    setCargandoLista(true);
    try {
      fetch(`${API_URL}/api/horarios/sedes`)
        .then((r) => r.json())
        .then((d) => { if (d?.data) setSedesCatalogo(d.data); })
        .catch(() => {});

      const urlsListar = [
        `${API_URL}/api/medidas-disciplinarias/listar`,
        `${API_URL}/api/th/medidas-disciplinarias/listar`,
        `${API_URL}/api/th/medidas/listar`
      ];

      let listaCargada = [];
      for (const url of urlsListar) {
        try {
          const response = await fetch(url, { headers: getHeaders() });
          if (response.ok) {
            const data = await response.json();
            const arr = Array.isArray(data) ? data : (data.data || data.medidas || data.registros || []);
            if (Array.isArray(arr)) {
              listaCargada = arr;
              break;
            }
          }
        } catch (errUrl) {}
      }
      setMedidas(listaCargada);
    } catch (error) {
      console.error('Error al cargar medidas:', error);
    } finally {
      setCargandoLista(false);
    }
  }, [API_URL, getHeaders]);

  useEffect(() => {
    cargarMedidas();
  }, [cargarMedidas]);

  const abrirModalNuevoRegistro = () => {
    setFormData({
      documento_empleado: '',
      nombres_empleado: '',
      cargo: '',
      sede: !configABAC.esGlobal && configABAC.sedePropiaUsuario ? configABAC.sedePropiaUsuario : '',
      tipo_medida: 'Llamado de Atención',
      observaciones: '',
      fecha_incidencia: new Date().toISOString().split('T')[0],
    });
    setArchivoBase64('');
    setNombreArchivo('');
    setModalAbierto(true);
  };

  // =========================================================
  // 3. AUTOCOMPLETADO DE EMPLEADO CON VALIDACIÓN DE SEDE ABAC
  // =========================================================
  const buscarEmpleadoPorCedula = async (cedula) => {
    const doc = String(cedula || formData.documento_empleado || '').trim();
    if (!doc) return;
    setBuscandoEmp(true);
    try {
      const urlsBusqueda = [
        `${API_URL}/api/usuarios/buscar?query=${encodeURIComponent(doc)}`,
        `${API_URL}/api/empleados/buscar?query=${encodeURIComponent(doc)}`,
        `${API_URL}/api/horarios/empleados`
      ];

      let empEncontrado = null;
      for (const url of urlsBusqueda) {
        try {
          const res = await fetch(url, { headers: getHeaders() });
          if (!res.ok) continue;
          const data = await res.json();

          if (url.endsWith('/empleados')) {
            const lista = Array.isArray(data) ? data : (data.data || []);
            empEncontrado = lista.find((e) => String(e.documento || e.terc_nit || '').trim() === doc);
          } else {
            const candidato = Array.isArray(data) ? data[0] : (data.usuario || data.empleado || data.data?.[0] || data);
            if (candidato && (candidato.nombres || candidato.terc_nom || candidato.nombre_completo)) {
              empEncontrado = candidato;
            }
          }
          if (empEncontrado) break;
        } catch (e) {}
      }

      if (empEncontrado) {
        const sedeEmp = String(
          empEncontrado.sede_nombre ||
          empEncontrado.area_nombre ||
          empEncontrado.sede_original ||
          empEncontrado.sede ||
          empEncontrado.centro_trabajo ||
          'OFICINAS'
        ).toUpperCase().trim();

        if (!sedeEstaAutorizada(sedeEmp, empEncontrado.sede_id)) {
          mostrarAviso(`⛔ Acceso ABAC: El colaborador pertenece a la sede "${sedeEmp}" y tu perfil solo tiene alcance sobre tu sede asignada.`, 'error');
          setFormData((prev) => ({ ...prev, nombres_empleado: '', cargo: '', sede: '' }));
          return;
        }

        setFormData((prev) => ({
          ...prev,
          documento_empleado: empEncontrado.documento || empEncontrado.terc_nit || doc,
          nombres_empleado: empEncontrado.nombres || empEncontrado.terc_nom || empEncontrado.nombre_completo || prev.nombres_empleado,
          cargo: empEncontrado.cargo_nombre || empEncontrado.empl_cargo || empEncontrado.cargo_original || empEncontrado.cargo || 'SIN CARGO',
          sede: sedeEmp,
        }));
        mostrarAviso('✅ Datos del colaborador cargados correctamente.', 'success');
      } else {
        mostrarAviso('ℹ️ No se encontró la cédula en Sysplus, puedes completar los datos manualmente.', 'warning');
      }
    } catch (error) {
      console.error('Error buscando empleado en BD:', error);
    } finally {
      setBuscandoEmp(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // NUEVA LÓGICA DE ARCHIVOS: Soporta Fotos, PDF y Videos (hasta 40MB)
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validación de peso (40MB = 41943040 bytes)
    const MAX_SIZE_MB = 40;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      mostrarAviso(`⚠️ El archivo es demasiado grande. El peso máximo permitido es de ${MAX_SIZE_MB}MB.`, 'error');
      e.target.value = null; // Resetea el input
      setNombreArchivo('');
      setArchivoBase64('');
      return;
    }

    setNombreArchivo(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setArchivoBase64(String(reader.result || ''));
    };
    reader.readAsDataURL(file);
  };

  // =========================================================
  // 4. ENVÍO DEL FORMULARIO Y RECARGA AUTOMÁTICA EN VIVO
  // =========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.documento_empleado.trim() || !formData.nombres_empleado.trim() || !formData.observaciones.trim()) {
      mostrarAviso('⚠️ Completa la cédula, el nombre del colaborador y las observaciones.', 'error');
      return;
    }

    // Validación de evidencia obligatoria
    if (!archivoBase64) {
      mostrarAviso('⚠️ La evidencia (foto, documento o video corto) es obligatoria para registrar una medida disciplinaria.', 'error');
      return;
    }

    const sedeFinal = (formData.sede || configABAC.sedePropiaUsuario || 'OFICINAS').trim().toUpperCase();
    if (!sedeEstaAutorizada(sedeFinal)) {
      mostrarAviso(`⛔ No tienes permisos ABAC para registrar medidas en la sede "${sedeFinal}".`, 'error');
      return;
    }

    setCargando(true);
    try {
      const autor = usuario?.nombres
        ? `${usuario.nombres} ${usuario.apellidos || ''}`.trim()
        : (usuario?.nombre || usuario?.documento || 'Talento Humano');

      const payload = {
        documento_empleado: formData.documento_empleado.trim(),
        documento: formData.documento_empleado.trim(),
        nombres_empleado: formData.nombres_empleado.trim().toUpperCase(),
        nombre_empleado: formData.nombres_empleado.trim().toUpperCase(),
        cargo: (formData.cargo || 'OPERATIVO').trim().toUpperCase(),
        sede: sedeFinal,
        area_empleado: sedeFinal,
        tipo_medida: formData.tipo_medida,
        observaciones: formData.observaciones.trim(),
        motivo: formData.observaciones.trim(),
        descripcion: formData.observaciones.trim(),
        fecha_incidencia: formData.fecha_incidencia,
        fecha: formData.fecha_incidencia,
        reportado_por: `${autor}${usuario?.documento ? ` (${usuario.documento})` : ''}`,
        creado_por: `${autor}${usuario?.documento ? ` (${usuario.documento})` : ''}`,
        archivo_url: archivoBase64,
        evidencia_foto: archivoBase64,
        archivo_base64: archivoBase64,
      };

      const endpointsCrear = [
        `${API_URL}/api/medidas-disciplinarias/crear`,
        `${API_URL}/api/th/medidas-disciplinarias/crear`,
        `${API_URL}/api/th/medidas/crear`
      ];

      let guardadoExitoso = false;
      let ultimoError = 'No se encontró la ruta en el servidor';

      for (const url of endpointsCrear) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(payload),
          });
          const resData = await response.json().catch(() => ({}));
          if (response.ok && resData.success !== false) {
            guardadoExitoso = true;
            break;
          } else if (resData.message) {
            ultimoError = resData.message;
          }
        } catch (errPost) {
          ultimoError = errPost.message;
        }
      }

      if (guardadoExitoso) {
        setModalAbierto(false);
        setArchivoBase64('');
        setNombreArchivo('');
        mostrarAviso('✅ Medida disciplinaria y evidencia registradas exitosamente.', 'success');
        await cargarMedidas();
      } else {
        mostrarAviso(`❌ Error al guardar: ${ultimoError}`, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      mostrarAviso('❌ Error de conexión con el servidor.', 'error');
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (fechaRaw) => {
    if (!fechaRaw) return 'Sin fecha';
    const d = new Date(fechaRaw);
    if (isNaN(d.getTime())) return String(fechaRaw).split('T')[0];
    return d.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const obtenerEvidenciaValida = (medida) => {
    const raw = medida.archivo_url || medida.archivo_base64 || medida.evidencia_foto || medida.evidencia_url;
    if (!raw) return null;
    const str = String(raw).trim();
    if (!str || str.toUpperCase() === 'NULL' || str === 'undefined') return null;
    return str;
  };

  const medidasFiltradas = useMemo(() => {
    return medidas.filter((medida) => {
      const sedeMedida = medida.sede || medida.area_empleado || 'OFICINAS';
      if (!sedeEstaAutorizada(sedeMedida, medida.sede_id)) return false;

      if (filtroSedeUI && !String(sedeMedida).toUpperCase().includes(filtroSedeUI.toUpperCase())) {
        return false;
      }

      if (busquedaTexto.trim()) {
        const q = busquedaTexto.toLowerCase();
        const textoFila = `${medida.nombres_empleado || ''} ${medida.nombre_empleado || ''} ${medida.documento_empleado || ''} ${medida.cargo || ''} ${sedeMedida} ${medida.tipo_medida || ''} ${medida.observaciones || ''} ${medida.motivo || ''}`.toLowerCase();
        if (!textoFila.includes(q)) return false;
      }

      return true;
    });
  }, [medidas, sedeEstaAutorizada, filtroSedeUI, busquedaTexto]);

  const sedesDisponiblesFiltro = useMemo(() => {
    const setSedes = new Set();
    medidas.forEach((m) => {
      const s = String(m.sede || m.area_empleado || 'OFICINAS').toUpperCase().trim();
      if (sedeEstaAutorizada(s, m.sede_id)) setSedes.add(s);
    });
    sedesCatalogo.forEach((sc) => {
      if (sedeEstaAutorizada(sc.nombre, sc.id)) setSedes.add(String(sc.nombre).toUpperCase().trim());
    });
    return Array.from(setSedes).sort();
  }, [medidas, sedesCatalogo, sedeEstaAutorizada]);

  return (
    <div className="w-full px-2 py-3 sm:p-6 animate-fade-in relative z-10">
      <div className="bg-white/55 backdrop-blur-[35px] rounded-[24px] sm:rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-4 sm:p-8 max-w-6xl mx-auto border border-white/80">
        
        {/* CABECERA */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-slate-300/60 pb-4 mb-5 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center text-xl sm:text-2xl shadow-inner border border-amber-200 shrink-0">
              ⚖️
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">
                  Medidas Disciplinarias
                </h2>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase border ${
                  configABAC.esGlobal
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-blue-50 text-blue-700 border-blue-200'
                }`}>
                  {configABAC.esGlobal
                    ? '🌐 Todas las Sedes'
                    : `📍 Sede: ${configABAC.sedePropiaUsuario || 'Asignada'}`}
                </span>
              </div>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">
                Historial y Trazabilidad ({medidasFiltradas.length} registros visibles)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            {configABAC.puedeCrear && (
              <button
                type="button"
                onClick={abrirModalNuevoRegistro}
                className="flex-1 md:flex-initial px-4 sm:px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-amber-950 text-xs font-black uppercase tracking-wider transition-all shadow-md cursor-pointer text-center"
              >
                + Nuevo Registro
              </button>
            )}
            <button
              type="button"
              onClick={onBackToMenu}
              className="px-4 sm:px-5 py-2.5 rounded-xl bg-white/90 hover:bg-white text-slate-800 text-xs font-black uppercase tracking-wider transition-all border border-slate-200 shadow-sm cursor-pointer"
            >
              Volver
            </button>
          </div>
        </div>

        {/* NOTIFICACIÓN EN PANTALLA */}
        {notificacion.texto && (
          <div className={`mb-4 p-3.5 rounded-2xl text-xs font-black flex justify-between items-center border ${
            notificacion.tipo === 'error'
              ? 'bg-red-50 text-red-800 border-red-200'
              : notificacion.tipo === 'warning'
              ? 'bg-amber-50 text-amber-900 border-amber-200'
              : 'bg-emerald-50 text-emerald-800 border-emerald-200'
          }`}>
            <span>{notificacion.texto}</span>
            <button onClick={() => setNotificacion({ texto: '', tipo: '' })} className="ml-3 font-black">✕</button>
          </div>
        )}

        {/* BARRA DE BÚSQUEDA Y FILTRO */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <input
            type="text"
            placeholder="🔍 Buscar por nombre, cédula o motivo..."
            value={busquedaTexto}
            onChange={(e) => setBusquedaTexto(e.target.value)}
            className="flex-1 px-4 py-2.5 rounded-xl bg-white/90 border border-slate-200 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-amber-400"
          />

          {(configABAC.esGlobal || sedesDisponiblesFiltro.length > 1) && (
            <select
              value={filtroSedeUI}
              onChange={(e) => setFiltroSedeUI(e.target.value)}
              className="w-full sm:w-60 px-3.5 py-2.5 rounded-xl bg-white/90 border border-slate-200 text-xs font-black text-slate-700 outline-none cursor-pointer"
            >
              <option value="">🏢 Todas las sedes autorizadas</option>
              {sedesDisponiblesFiltro.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
        </div>

        {/* VISTA MÓVIL (TARJETAS) */}
        <div className="block md:hidden space-y-3">
          {cargandoLista ? (
            <div className="p-8 text-center text-slate-500 font-bold text-xs animate-pulse bg-white/70 rounded-2xl">
              Cargando medidas disciplinarias...
            </div>
          ) : medidasFiltradas.length === 0 ? (
            <div className="p-8 text-center text-slate-400 font-bold text-xs italic bg-white/70 rounded-2xl">
              No hay medidas disciplinarias registradas.
            </div>
          ) : (
            medidasFiltradas.map((medida) => {
              const evidenciaSrc = obtenerEvidenciaValida(medida);
              const nombreEmp = medida.nombres_empleado || medida.nombre_empleado || 'Colaborador';
              const docEmp = medida.documento_empleado || medida.documento || 'N/A';
              const cargoEmp = medida.cargo || medida.cargo_empleado || 'SIN CARGO';
              const sedeEmp = medida.sede || medida.area_empleado || 'OFICINAS';
              const obsTexto = medida.observaciones || medida.motivo || medida.descripcion || 'Sin observaciones';
              const reportadoTexto = medida.reportado_por || medida.creado_por || 'Talento Humano';
              const estaExpandido = observacionesExpandidas[medida.id];

              return (
                <div key={medida.id} className="bg-white/90 rounded-2xl p-4 border border-slate-200/80 shadow-sm space-y-2.5">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-black text-slate-900 text-xs uppercase">{nombreEmp}</p>
                      <p className="text-[10px] font-bold text-slate-500">CC: {docEmp} | {cargoEmp}</p>
                    </div>
                    <span className="bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-[10px] font-black border border-amber-200 shrink-0">
                      {medida.tipo_medida || 'Llamado de Atención'}
                    </span>
                  </div>

                  <div className="flex flex-wrap justify-between items-center text-[10px] font-bold text-slate-500 border-y border-slate-100 py-1.5">
                    <span className="text-blue-600 uppercase">📍 {sedeEmp}</span>
                    <span>📅 {formatearFecha(medida.fecha_incidencia || medida.fecha_registro || medida.fecha)}</span>
                  </div>

                  <div>
                    <p className={`text-xs text-slate-700 font-medium bg-slate-50 p-2.5 rounded-xl border border-slate-100 ${!estaExpandido ? 'line-clamp-3' : ''}`}>
                      {obsTexto}
                    </p>
                    {obsTexto.length > 90 && (
                      <button
                        type="button"
                        onClick={() => toggleExpandirObs(medida.id)}
                        className="text-[10px] font-black text-amber-600 mt-1 hover:underline cursor-pointer"
                      >
                        {estaExpandido ? '▲ Ver menos' : '▼ Ver completo'}
                      </button>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-1">
                    <span className="text-[10px] font-bold text-slate-500 truncate max-w-[60%]">
                      Por: {reportadoTexto}
                    </span>
                    {evidenciaSrc ? (
                      <button
                        type="button"
                        onClick={() => setEvidenciaModal({ src: evidenciaSrc, empleado: nombreEmp, obs: obsTexto })}
                        className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black uppercase border border-blue-200 cursor-pointer"
                      >
                        🖼️ Ver Evidencia
                      </button>
                    ) : (
                      <span className="text-rose-500 text-[10px] font-bold">⚠️ Sin evidencia</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* VISTA PC Y TABLET (TABLA CON EXPANSIÓN DE OBSERVACIONES) */}
        <div className="hidden md:block overflow-x-auto bg-white/80 backdrop-blur-md rounded-2xl border border-white shadow-sm">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-800 text-white text-[10px] font-black uppercase tracking-wider">
                <th className="p-3.5 rounded-tl-2xl">Fecha</th>
                <th className="p-3.5">Colaborador</th>
                <th className="p-3.5">Cargo / Sede</th>
                <th className="p-3.5">Tipo de Medida</th>
                <th className="p-3.5 w-1/3">Observaciones / Motivo</th>
                <th className="p-3.5">Reportado Por</th>
                <th className="p-3.5 rounded-tr-2xl text-center">Evidencia</th>
              </tr>
            </thead>
            <tbody className="text-xs text-slate-700 divide-y divide-slate-200/60">
              {cargandoLista ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-slate-400 font-bold animate-pulse">
                    Cargando historial de medidas disciplinarias...
                  </td>
                </tr>
              ) : medidasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-slate-400 font-bold italic">
                    No hay medidas disciplinarias registradas para esta sede.
                  </td>
                </tr>
              ) : (
                medidasFiltradas.map((medida) => {
                  const evidenciaSrc = obtenerEvidenciaValida(medida);
                  const nombreEmp = medida.nombres_empleado || medida.nombre_empleado || 'Colaborador';
                  const docEmp = medida.documento_empleado || medida.documento || 'N/A';
                  const cargoEmp = medida.cargo || medida.cargo_empleado || 'SIN CARGO';
                  const sedeEmp = medida.sede || medida.area_empleado || 'OFICINAS';
                  const obsTexto = medida.observaciones || medida.motivo || medida.descripcion || 'Sin observaciones';
                  const reportadoTexto = medida.reportado_por || medida.creado_por || 'Talento Humano';
                  const estaExpandido = observacionesExpandidas[medida.id];

                  return (
                    <tr key={medida.id} className="hover:bg-amber-50/40 transition-colors align-top">
                      <td className="p-3.5 font-bold text-slate-700 whitespace-nowrap">
                        {formatearFecha(medida.fecha_incidencia || medida.fecha_registro || medida.fecha)}
                      </td>

                      <td className="p-3.5">
                        <p className="font-black text-slate-900 uppercase leading-tight">{nombreEmp}</p>
                        <p className="text-[10px] font-bold text-slate-500 mt-0.5">CC: {docEmp}</p>
                      </td>

                      <td className="p-3.5">
                        <span className="inline-block font-extrabold text-slate-800 uppercase text-[11px]">{cargoEmp}</span>
                        <span className="block text-[10px] font-bold text-blue-600 uppercase">📍 {sedeEmp}</span>
                      </td>

                      <td className="p-3.5">
                        <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-[10px] font-black tracking-wider border border-amber-200 whitespace-nowrap">
                          {medida.tipo_medida || 'Llamado de Atención'}
                        </span>
                      </td>

                      <td className="p-3.5">
                        <div className={`text-xs text-slate-700 font-medium bg-slate-50/80 p-2 rounded-xl border border-slate-200/50 ${!estaExpandido ? 'line-clamp-2' : ''}`}>
                          {obsTexto}
                        </div>
                        {obsTexto.length > 70 && (
                          <button
                            type="button"
                            onClick={() => toggleExpandirObs(medida.id)}
                            className="text-[10px] font-black text-amber-600 mt-1 hover:text-amber-700 cursor-pointer block"
                          >
                            {estaExpandido ? '▲ Ver menos' : '▼ Ver completo'}
                          </button>
                        )}
                      </td>

                      <td className="p-3.5 text-[11px] font-bold text-slate-700">
                        {reportadoTexto}
                      </td>

                      <td className="p-3.5 text-center">
                        {evidenciaSrc ? (
                          <button
                            type="button"
                            onClick={() => setEvidenciaModal({ src: evidenciaSrc, empleado: nombreEmp, obs: obsTexto })}
                            className="w-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-wider border border-blue-200 transition cursor-pointer shadow-sm"
                          >
                            🖼️ Ver Evidencia
                          </button>
                        ) : (
                          <span className="text-rose-500 text-[10px] font-bold">⚠️ Sin evidencia</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL PARA VER EVIDENCIA MULTIMEDIA (FOTOS, PDF, VIDEO) */}
      {evidenciaModal && (
        <div className="fixed inset-0 z-[99999] bg-slate-900/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white flex flex-col my-auto">
            <div className="flex justify-between items-center pb-3 mb-4 border-b border-slate-200">
              <div>
                <h4 className="text-sm sm:text-base font-black text-slate-800 uppercase">Evidencia Adjunta</h4>
                <p className="text-xs font-bold text-slate-500">{evidenciaModal.empleado}</p>
              </div>
              <button
                type="button"
                onClick={() => setEvidenciaModal(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-rose-100 text-slate-600 hover:text-rose-600 font-black cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="bg-slate-100 rounded-2xl p-2 sm:p-3 flex items-center justify-center max-h-[60vh] overflow-auto">
              {/* LÓGICA DE REPRODUCCIÓN CONDICIONAL SEGÚN TIPO MIME */}
              {String(evidenciaModal.src).startsWith('data:video') || String(evidenciaModal.src).match(/\.(mp4|mov|webm)$/i) ? (
                <video controls className="w-full max-h-[55vh] rounded-xl shadow bg-black">
                  <source src={String(evidenciaModal.src).startsWith('data:') ? evidenciaModal.src : `${API_URL}${evidenciaModal.src}`} />
                  Tu navegador no soporta la reproducción de este video.
                </video>
              ) : String(evidenciaModal.src).startsWith('data:image') || String(evidenciaModal.src).match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                <img src={evidenciaModal.src} alt="Evidencia" className="max-h-[55vh] w-auto object-contain rounded-xl shadow" />
              ) : (
                <iframe
                  src={String(evidenciaModal.src).startsWith('data:') ? evidenciaModal.src : `${API_URL}${evidenciaModal.src}`}
                  title="Evidencia Documento"
                  className="w-full h-[50vh] rounded-xl"
                />
              )}
            </div>

            {evidenciaModal.obs && (
              <p className="mt-4 text-xs text-slate-600 bg-amber-50 p-3 rounded-xl border border-amber-200 font-medium">
                <strong>Observación registrada:</strong> {evidenciaModal.obs}
              </p>
            )}
          </div>
        </div>
      )}

      {/* MODAL PARA CREAR REGISTRO */}
      {modalAbierto && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/65 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-[24px] sm:rounded-[28px] shadow-2xl p-5 sm:p-7 w-full max-w-xl max-h-[92vh] overflow-y-auto border border-gray-100 my-auto">
            <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-base sm:text-lg font-black text-slate-800">Registrar Medida Disciplinaria</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase">
                  {configABAC.esGlobal ? 'Alcance Global Autorizado' : `Sede Vinculada: ${configABAC.sedePropiaUsuario || 'Sede Actual'}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalAbierto(false)}
                className="w-8 h-8 bg-gray-100 rounded-full text-gray-600 font-bold hover:bg-red-100 hover:text-red-600 transition cursor-pointer shrink-0"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">
                  Cédula del Colaborador (Autocompleta desde Sysplus)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="documento_empleado"
                    required
                    value={formData.documento_empleado}
                    onChange={handleInputChange}
                    onBlur={(e) => buscarEmpleadoPorCedula(e.target.value)}
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold focus:border-amber-500 outline-none"
                    placeholder="Escribe la cédula..."
                  />
                  <button
                    type="button"
                    onClick={() => buscarEmpleadoPorCedula(formData.documento_empleado)}
                    disabled={buscandoEmp}
                    className="px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-wider cursor-pointer shrink-0"
                  >
                    {buscandoEmp ? '...' : '🔍 Buscar'}
                  </button>
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Nombre Completo *</label>
                <input
                  type="text"
                  name="nombres_empleado"
                  required
                  value={formData.nombres_empleado}
                  onChange={handleInputChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold uppercase focus:border-amber-500 outline-none"
                  placeholder="Se autocompleta al buscar cédula..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Cargo</label>
                <input
                  type="text"
                  name="cargo"
                  value={formData.cargo}
                  onChange={handleInputChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold uppercase focus:border-amber-500 outline-none"
                  placeholder="Ej: CAJERO / SURTIDOR"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Sede / Punto *</label>
                <input
                  type="text"
                  name="sede"
                  required
                  readOnly={!configABAC.esGlobal && Boolean(configABAC.sedePropiaUsuario)}
                  value={formData.sede}
                  onChange={handleInputChange}
                  className={`w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold uppercase outline-none ${
                    !configABAC.esGlobal && configABAC.sedePropiaUsuario ? 'bg-slate-200/70 text-slate-600' : 'bg-slate-50 focus:border-amber-500'
                  }`}
                  placeholder="Ej: BALMORAL"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tipo de Medida</label>
                <select
                  name="tipo_medida"
                  value={formData.tipo_medida}
                  onChange={handleInputChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold focus:border-amber-500 outline-none"
                >
                  <option value="Llamado de Atención">Llamado de Atención</option>
                  <option value="Acta de Descargos">Acta de Descargos</option>
                  <option value="Suspensión">Suspensión</option>
                  <option value="Terminación de Contrato">Terminación de Contrato</option>
                  <option value="Llamado a la mejora">Llamado a la mejora</option>
                  <option value="Remisión a Talento Humano">Remisión a Talento Humano</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Fecha Incidencia</label>
                <input
                  type="date"
                  name="fecha_incidencia"
                  required
                  value={formData.fecha_incidencia}
                  onChange={handleInputChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold focus:border-amber-500 outline-none"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Observaciones / Hechos *</label>
                <textarea
                  name="observaciones"
                  required
                  rows="3"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium focus:border-amber-500 outline-none resize-none"
                  placeholder="Describe los hechos o la novedad evidenciada..."
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">
                  Evidencia Fotográfica / Documento / Video <span className="text-rose-500">(Máx 40MB) *</span>
                </label>
                <div className="w-full border-2 border-dashed border-slate-300 rounded-xl p-3 text-center hover:bg-slate-50 transition">
                  <input
                    type="file"
                    required
                    accept="image/*,.pdf,video/mp4,video/quicktime,video/webm"
                    onChange={handleFileChange}
                    className="w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-black file:bg-amber-100 file:text-amber-800 cursor-pointer"
                  />
                  {nombreArchivo ? (
                    <p className="text-[10px] font-bold text-emerald-600 mt-1.5">
                      ✓ Evidencia cargada lista para enviar: {nombreArchivo}
                    </p>
                  ) : (
                    <p className="text-[10px] font-bold text-rose-500 mt-1.5">
                      Debe adjuntar una imagen, PDF o Video corto (MP4) como soporte para radicar.
                    </p>
                  )}
                </div>
              </div>

              <div className="sm:col-span-2 pt-2 flex gap-2.5">
                <button
                  type="button"
                  onClick={() => setModalAbierto(false)}
                  className="px-5 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-slate-700 font-bold text-xs cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={cargando}
                  className="flex-1 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-black tracking-widest uppercase text-xs transition disabled:opacity-50 cursor-pointer shadow-md"
                >
                  {cargando ? 'Guardando...' : '💾 Guardar Medida Disciplinaria'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}